import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { handleLoginSuccess } from '../utils/androidBridge';
import { 
  BuildingOfficeIcon,
  LockClosedIcon,
  UserIcon,
  PhoneIcon,
  GlobeAltIcon,
  LinkIcon
} from '@heroicons/react/24/outline';
import { ArrowRightIcon } from '@heroicons/react/24/solid';
import useAuthStore from '../store/authStore';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, user, isLoading } = useAuthStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const formRef = useRef(null);

  const { register, handleSubmit, setValue, formState: { errors } } = useForm();

  // 로그인 후 이동할 경로
  const from = location.state?.from || '/';

  // 리다이렉트 effect - 인증 정보 확인
  useEffect(() => {
    // localStorage에서 직접 확인
    const storedToken = localStorage.getItem('token');
    const authStorage = localStorage.getItem('auth-storage');
    
    console.log('Login Page Check:', {
      user: !!user,
      storedToken: !!storedToken,
      authStorage: !!authStorage,
      from: from
    });
    
    // auth-storage 확인
    let hasValidAuth = false;
    if (authStorage) {
      try {
        const authData = JSON.parse(authStorage);
        if (authData.state?.token && authData.state?.user) {
          hasValidAuth = true;
        }
        console.log('Login Auth Storage:', {
          hasToken: !!authData.state?.token,
          hasUser: !!authData.state?.user
        });
      } catch (e) {
        console.error('Login Auth Parse Error:', e);
      }
    }
    
    // 유효한 인증 정보가 있으면 홈으로
    if (user || storedToken || hasValidAuth) {
      console.log('Login: Redirecting to', from);
      navigate(from);
    } else {
      console.log('Login: No auth found, staying on login page');
    }
  }, [user, navigate, from]);

  // 모바일에서 입력 포커스 시 스크롤
  useEffect(() => {
    if (isInputFocused && formRef.current) {
      const isMobile = window.innerWidth <= 768;
      if (isMobile) {
        setTimeout(() => {
          formRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 300);
      }
    }
  }, [isInputFocused]);

  const handleInputFocus = () => {
    setIsInputFocused(true);
  };

  const handleInputBlur = () => {
    setTimeout(() => {
      setIsInputFocused(false);
    }, 200);
  };

  const [showDeactivatedModal, setShowDeactivatedModal] = useState(false);
  const [deactivatedInfo, setDeactivatedInfo] = useState(null);

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const result = await login(data);
      if (result.success) {
        toast.success('로그인 성공!');
        navigate(from);
      } else {
        // 에러 타입에 따른 처리
        if (result.errorType === 'password_incorrect') {
          toast.error('비밀번호가 틀렸습니다.');
        } else if (result.errorType === 'account_deactivated') {
          setDeactivatedInfo(result.deactivatedAt);
          setShowDeactivatedModal(true);
        } else {
          toast.error(result.error || '로그인에 실패했습니다.');
        }
      }
    } catch (error) {
      toast.error('로그인 중 오류가 발생했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* 배경 패턴 */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full opacity-40 blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-full opacity-40 blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[40rem] h-[40rem] bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 rounded-full opacity-50 blur-3xl"></div>
      </div>

      <div className="relative z-10 flex-1 flex items-center justify-center px-4 py-8 sm:py-12">
        <div className="w-full max-w-lg">
          {/* 로고 영역 */}
          <div className="text-center mb-6 sm:mb-8">
            <div className="inline-flex items-center justify-center mb-5">
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-indigo-500 blur-xl opacity-20 group-hover:opacity-30 transition-opacity duration-300"></div>
                <img 
                  src="/images/logo-geongi.png" 
                  alt="건기넷" 
                  className="h-16 sm:h-20 relative z-10 transform group-hover:scale-105 transition-transform duration-300"
                />
              </div>
            </div>
            
            <h1 className="text-4xl sm:text-5xl font-black bg-gradient-to-r from-gray-800 to-gray-700 bg-clip-text text-transparent mb-2" 
                style={{ fontFamily: 'GmarketSansBold', letterSpacing: '-0.02em' }}>
              건기프로젝트
            </h1>
            <p className="text-base sm:text-lg text-gray-600 font-medium" style={{ fontFamily: 'GmarketSansMedium' }}>
              건기 프로젝트 통합관리 시스템
            </p>
          </div>

          {/* 로그인 폼 */}
          <div ref={formRef} className="bg-white rounded-2xl sm:rounded-3xl shadow-lg sm:shadow-xl overflow-hidden border border-gray-200">
            <div className="p-6 sm:p-8">
              <form className="space-y-5 sm:space-y-6" onSubmit={handleSubmit(onSubmit)}>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2" style={{ fontFamily: 'GmarketSansMedium' }}>
                    아이디
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                      <UserIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      {...register('email', { required: '아이디를 입력해주세요' })}
                      type="text"
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                      className="w-full pl-11 pr-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl focus:bg-white focus:border-blue-500 focus:outline-none transition-all duration-200 text-gray-900 placeholder-gray-400"
                      placeholder="아이디 또는 이메일 입력"
                    />
                  </div>
                  {errors.email && (
                    <p className="mt-2 text-sm text-red-600 flex items-center animate-fadeIn">
                      <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors.email.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2" style={{ fontFamily: 'GmarketSansMedium' }}>
                    비밀번호
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                      <LockClosedIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      {...register('password', { required: '비밀번호를 입력해주세요' })}
                      type="password"
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                      className="w-full pl-11 pr-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl focus:bg-white focus:border-blue-500 focus:outline-none transition-all duration-200 text-gray-900 placeholder-gray-400"
                      placeholder="비밀번호 입력"
                    />
                  </div>
                  {errors.password && (
                    <p className="mt-2 text-sm text-red-600 flex items-center animate-fadeIn">
                      <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors.password.message}
                    </p>
                  )}
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={isSubmitting || isLoading}
                    className="relative w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold py-3.5 px-6 rounded-xl shadow-md hover:shadow-lg hover:from-blue-700 hover:to-indigo-700 transform hover:-translate-y-0.5 transition-all duration-200 overflow-hidden group disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:transform-none"
                    style={{ fontFamily: 'GmarketSansBold' }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                    <span className="relative text-base sm:text-lg flex items-center justify-center">
                      {isSubmitting || isLoading ? (
                        <span className="flex items-center">
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          로그인 중...
                        </span>
                      ) : (
                        <>
                          로그인
                          <ArrowRightIcon className="ml-2 h-4 w-4 sm:h-5 sm:w-5 group-hover:translate-x-1 transition-transform duration-200" />
                        </>
                      )}
                    </span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Footer - 미니멀하고 은은한 디자인 */}
      <footer className="relative z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 sm:py-4">
          {/* 모바일 레이아웃 */}
          <div className="sm:hidden">
            <div className="text-center space-y-1.5">
              <a href="tel:1566-0622" className="inline-flex items-center text-gray-400 hover:text-gray-600 transition-colors text-xs">
                <PhoneIcon className="h-3 w-3 mr-1" />
                <span>1566-0622</span>
              </a>
              <div className="flex justify-center items-center space-x-3 text-xs text-gray-400">
                <a href="https://geongi.net" target="_blank" rel="noopener noreferrer" 
                   className="hover:text-gray-600 transition-colors">
                  건기넷
                </a>
                <span className="text-gray-300">•</span>
                <a href="https://polishing.kr" target="_blank" rel="noopener noreferrer" 
                   className="hover:text-gray-600 transition-colors">
                  건기폴리싱
                </a>
              </div>
            </div>
          </div>

          {/* 데스크톱 레이아웃 */}
          <div className="hidden sm:block">
            <div className="flex items-center justify-center space-x-6 text-xs text-gray-400">
              <a href="tel:1566-0622" className="inline-flex items-center hover:text-gray-600 transition-colors">
                <PhoneIcon className="h-3.5 w-3.5 mr-1.5" />
                <span>1566-0622</span>
              </a>
              <span className="text-gray-300">•</span>
              <div className="flex items-center">
                <span className="mr-1.5">건기넷</span>
                <a href="https://geongi.net" target="_blank" rel="noopener noreferrer" 
                   className="hover:text-gray-600 transition-colors">
                  https://geongi.net
                </a>
              </div>
              <span className="text-gray-300">•</span>
              <div className="flex items-center">
                <span className="mr-1.5">건기폴리싱</span>
                <a href="https://polishing.kr" target="_blank" rel="noopener noreferrer" 
                   className="hover:text-gray-600 transition-colors">
                  https://polishing.kr
                </a>
              </div>
            </div>
          </div>

          {/* 저작권 */}
          <div className="mt-2 text-center">
            <p className="text-xs text-gray-300">
              © 2025 건기프로젝트. 모든 권리 보유.
            </p>
          </div>
        </div>
      </footer>

      {/* 비활성화 모달 */}
      {showDeactivatedModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full overflow-hidden shadow-2xl">
            {/* 헤더 */}
            <div className="bg-gradient-to-r from-red-500 to-red-600 p-6 text-white">
              <div className="flex items-center gap-3">
                <div className="bg-white/20 rounded-full p-3">
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold">계정 이용 제한 안내</h3>
              </div>
            </div>
            
            {/* 내용 */}
            <div className="p-6 space-y-4">
              <div className="text-center">
                <p className="text-lg font-semibold text-gray-800 mb-2">
                  소장님의 계정이 일시적으로 비활성화되었습니다.
                </p>
                {deactivatedInfo && (
                  <p className="text-sm text-gray-500">
                    비활성화 일시: {new Date(deactivatedInfo).toLocaleDateString('ko-KR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                )}
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <p className="text-sm font-medium text-gray-700">비활성화 주요 사유:</p>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-red-500 mt-0.5">•</span>
                    <span>본사 요청사항 미처리</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-500 mt-0.5">•</span>
                    <span>업무 진행 중단 또는 지연</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-500 mt-0.5">•</span>
                    <span>필수 서류 미제출</span>
                  </li>
                </ul>
              </div>
              
              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-sm font-medium text-blue-900 mb-2">
                  계정 재활성화를 원하시면 담당 관리자에게 문의해 주세요.
                </p>
                <div className="flex items-center justify-center gap-2 mt-3">
                  <PhoneIcon className="h-5 w-5 text-blue-600" />
                  <a href="tel:01566-0622" className="text-lg font-bold text-blue-600 hover:text-blue-700">
                    1566-0622
                  </a>
                </div>
                <p className="text-xs text-center text-gray-500 mt-2">
                  건기넷 고객센터
                </p>
              </div>
            </div>
            
            {/* 버튼 */}
            <div className="p-6 pt-0">
              <button
                onClick={() => setShowDeactivatedModal(false)}
                className="w-full bg-gray-800 hover:bg-gray-900 text-white font-medium py-3 rounded-xl transition-colors"
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default Login;